import type { HomeCopy } from '../types/home';

export const homeFr: HomeCopy = {
  hero: {
    eyebrow: 'Pour restaurants, cafés et bars',
    title: {
      line1: 'Votre menu,',
      pre: 'sur un ',
      highlight: 'QR code',
      post: ',',
      line3: 'en quelques minutes.',
    },
    subtitle: {
      intro:
        'Vos clients scannent une petite carte posée sur la table et votre menu s’ouvre sur leur téléphone. ',
      easyToUse: 'Facile à utiliser',
      comma: ',',
      safeForGuests: 'sûr pour vos clients',
      and: 'et',
      kindToNature: 'respectueux de la nature',
      outro: '. Pas d’appli, pas de réimpression, aucune complication.',
    },
    primaryCta: 'Commencez gratuitement - sans carte bancaire',
    secondaryCta: 'Voir comment ça marche',
    checklist: [
      'Prêt en ~15 minutes',
      'Fonctionne sur tous les téléphones',
      'Forfait gratuit inclus',
    ],
  },
  trustStrip: {
    ariaLabel: 'Pourquoi les restaurants choisissent SafestMenu',
    words: [
      'Facile à utiliser',
      'Approuvé par les clients',
      'Sûr et hygiénique',
      'Respectueux de la nature',
      'Aucune appli nécessaire',
      'Gratuit pour commencer',
    ],
  },
  why: {
    eyebrow: 'Pourquoi abandonner le papier',
    heading:
      'Les menus papier coûtent de l’argent, propagent les microbes et finissent à la poubelle.',
    cards: [
      {
        alt: 'Une table de restaurant impeccable avec un petit support QR code à la place des menus papier',
        title: 'Un menu que personne d’autre n’a touché',
        text: {
          pre: 'Un menu papier passe entre des centaines de mains et n’est presque jamais lavé. Avec un QR code, chaque client consulte son propre téléphone - c’est le',
          highlight: 'menu le plus hygiénique',
          post: 'qui soit.',
        },
      },
      {
        alt: 'Un restaurateur qui met à jour son menu depuis un téléphone au passe-plat',
        title: 'Changez un prix en 30 secondes',
        text: {
          pre: 'Nouveau plat\u00A0? Le fournisseur a augmenté ses prix\u00A0? Modifiez-le depuis votre téléphone et chaque table voit la mise à jour à l’instant. C’est vraiment',
          highlight: 'facile à utiliser',
          post: ' - si vous savez écrire un texto, vous savez utiliser SafestMenu.',
        },
      },
      {
        alt: 'Une pile de menus papier usés à côté d’un seul support QR code réutilisable et d’une plante verte',
        title: 'Imprimez une fois, plus jamais',
        text: {
          pre: 'Chaque saison de menus réimprimés et plastifiés, c’est de l’argent et du papier à la poubelle. Une seule petite carte par table est',
          highlight: 'respectueuse de la nature',
          post: '- et de votre budget.',
        },
      },
    ],
  },
  howItWorks: {
    eyebrow: 'Comment ça marche',
    heading: 'Trois étapes. Aucune compétence technique requise.',
    steps: [
      {
        title: 'Saisissez votre menu',
        text: 'Ajoutez vos plats et leurs prix. En panne d’inspiration\u00A0? Notre IA rédige des descriptions appétissantes et crée même des photos pour les plats qui n’en ont pas.',
      },
      {
        title: 'Imprimez votre QR code',
        text: 'Téléchargez des cartes de table prêtes à imprimer, dans tous les formats courants. Posez-les sur les tables, en vitrine, au comptoir - partout où vos clients s’installent.',
      },
      {
        title: 'Vos clients scannent et commandent',
        text: 'Aucune appli à installer. Le menu s’ouvre dans le navigateur du téléphone - avec photos, informations sur les allergènes et dans leur propre langue.',
      },
    ],
  },
  scanImport: {
    eyebrow: 'Nouveau - importez en quelques minutes',
    heading: 'Vous avez déjà un menu\u00A0? Partez de là.',
    paragraph:
      'Pas besoin de tout ressaisir. Téléversez un PDF ou quelques photos de votre menu imprimé, et SafestMenu le lit - plats, prix, sections - puis vous propose un brouillon propre à vérifier et publier. Un menu classique tient dans les crédits gratuits offerts à l’inscription.',
    steps: [
      {
        title: 'Téléversez votre menu',
        text: 'Déposez un PDF ou jusqu’à 40 photos. Vous voyez le coût exact à l’avance - 2 crédits par page - avant que rien ne soit facturé.',
      },
      {
        title: 'L’IA lit chaque page',
        text: 'Chaque page devient des plats, prix et sections modifiables, avec la langue et la devise du menu détectées automatiquement.',
      },
      {
        title: 'Vérifiez et publiez',
        text: 'Arrivez sur un écran de vérification bien organisé, ajustez ce qu’il faut, puis créez un nouveau menu ou ajoutez les pages à un menu existant.',
      },
    ],
  },
  productTour: {
    eyebrow: 'Voyez-le en action',
    heading: 'Regardez un menu prendre forme.',
    paragraph:
      'D’une page vide à la table, en direct. Voici le véritable éditeur - ajoutez un plat, laissez l’IA rédiger la description, réorganisez les éléments, publiez. Aucun appel de démonstration nécessaire.',
  },
  aiStudio: {
    eyebrow: 'IA intégrée',
    heading: {
      pre: 'Pas de photographe\u00A0?',
      highlight: 'Pas de souci.',
    },
    paragraph:
      'Prenez une photo rapide - ou aucune - et laissez SafestMenu générer de belles photos de plats, toutes cohérentes entre elles. Elle rédige aussi les descriptions appétissantes. Vous gardez le contrôle - choisissez la photo qui vous plaît, gardez-la, et c’est fait.',
    pills: [
      'Descriptions IA · 1 crédit',
      'Photos de plats IA · 5 crédits',
      'Traductions du menu · 1 crédit chacune',
      'Scan de menu · 2 crédits/page',
      'Suggestions d’allergènes IA · 1 crédit',
      'Les crédits n’expirent jamais',
    ],
  },
  features: {
    eyebrow: 'Tout est inclus',
    heading: 'Conçu pour les restaurateurs, pas pour les services informatiques.',
    sidebarCaption:
      'Chaque vue de plat est comptée automatiquement - pour que vous sachiez ce que vos clients préfèrent et mettiez vos best-sellers en avant.',
    items: [
      {
        title: 'Les prix se mettent à jour instantanément',
        text: 'Modifiez tout depuis votre téléphone. Chaque QR code, sur chaque table, affiche toujours le menu à jour.',
      },
      {
        title: 'Photos et descriptions par IA',
        text: 'Pas de photographe\u00A0? L’IA intégrée rédige des descriptions gourmandes et génère des photos de vos plats pour vous.',
      },
      {
        title: 'Les 14 allergènes de l’UE',
        text: 'Indiquez les allergènes de chaque plat une seule fois. Vos clients voient des étiquettes claires à table - fini les suppositions et les réponses risquées.',
      },
      {
        title: 'Six langues intégrées',
        text: 'Les touristes sont des clients comme les autres. Votre menu parle anglais, allemand, espagnol, français, italien et roumain.',
      },
      {
        title: 'Traduisez votre menu en un clic',
        text: 'L’IA traduit chaque plat dans le ton de votre menu, pour un crédit chacun. Modifiez un plat plus tard, et seul ce plat est retraduit - toujours à jour pour quelques centimes.',
      },
      {
        title: 'Des menus qui changent selon l’horaire',
        text: 'Gérez des cartes distinctes pour le petit-déjeuner, le déjeuner et le dîner. Réglez les horaires une fois pour toutes - le même QR code affiche toujours le menu du moment.',
      },
      {
        title: 'Dupliquez n’importe quel menu',
        text: 'Copiez un menu entier avec ses plats et ses photos, ajustez ce qui a changé, publiez quand vous êtes prêt. Pratique pour les cartes saisonnières et les secondes adresses.',
      },
      {
        title: 'Découvrez ce que vos clients préfèrent',
        text: 'Des statistiques simples montrent les plats les plus consultés - pour savoir quoi mettre à l’ardoise du jour.',
      },
      {
        title: 'Votre style, votre marque',
        text: 'Choisissez vos couleurs et vos polices, ajoutez votre logo et vos photos de plats. Votre menu ressemble à votre restaurant, pas au nôtre.',
      },
      {
        title: 'Partez de votre menu papier',
        text: 'Vous avez déjà un menu imprimé\u00A0? Téléversez un PDF ou quelques photos, et l’application le transforme en plats et prix modifiables - un menu classique tient dans vos crédits gratuits d’inscription.',
      },
      {
        title: 'L’IA suggère les allergènes',
        text: 'Vous modifiez un plat\u00A0? Laissez l’IA lire son nom et sa description pour proposer les 14 allergènes de l’UE, pour un crédit. Vous vérifiez et confirmez - un coup de pouce, jamais le dernier mot.',
      },
      {
        title: 'Vos clients filtrent le menu eux-mêmes',
        text: 'À table, vos clients ouvrent un filtre, choisissent ce qu’ils veulent éviter ou leur régime alimentaire, et les plats non adaptés s’effacent. Intégré, toujours actif, rien à configurer.',
      },
      {
        title: 'Transformez vos clients en avis Google',
        text: 'Ajoutez votre lien d’avis Google, et votre menu se termine par une invitation amicale - vos clients satisfaits cliquent et laissent un avis directement à table, pendant que le repas est encore frais en mémoire.',
      },
    ],
  },
  guests: {
    eyebrow: 'Vos clients adorent ça',
    heading: {
      pre: 'Un menu auquel vos clients peuvent vraiment faire',
      highlight: 'confiance',
      post: '.',
    },
    paragraph:
      'Des photos claires, des prix honnêtes, les allergènes indiqués sur chaque plat, et tout le menu dans leur propre langue. Vos clients commandent en toute confiance - et des clients confiants commandent davantage.',
    checklist: [
      'S’ouvre dans le navigateur - rien à installer',
      'Étiquettes d’allergènes sur chaque plat, sans exception',
      'D’un geste, tout le menu bascule dans leur langue',
    ],
  },
  pricing: {
    eyebrow: 'Tarifs',
    heading: 'Commencez gratuitement. Restez gratuit si cela vous suffit.',
    paragraph:
      'Votre premier menu avec cinq plats est gratuit - sans carte bancaire. Quand votre menu grandit, passez à des plats illimités et plus de crédits IA, pour les descriptions, les photos et les traductions du menu. Les six langues sont incluses, quel que soit votre forfait. Résiliez à tout moment.',
    price: '0 €',
    priceCaption: 'pour commencer',
    cta: 'Créez votre menu gratuit',
    disclaimer: 'Aucune carte bancaire. Aucune limite de temps.',
  },
  faq: {
    eyebrow: 'Questions fréquentes',
    heading: 'Ce que les restaurateurs demandent à propos de SafestMenu.',
    items: [
      {
        question: 'Qu’est-ce que SafestMenu et quel est son site officiel ?',
        answer:
          'SafestMenu est une plateforme de menus QR code pour restaurants, cafés et bars. Son site officiel est safestmenu.com, et les restaurateurs gèrent leurs menus sur app.safestmenu.com.',
      },
      {
        question: 'Comment fonctionne un menu QR code SafestMenu ?',
        answer:
          'Vous créez et publiez un menu numérique, puis imprimez son QR code pour vos tables. Vos clients le scannent avec l’appareil photo de leur téléphone et le menu à jour s’ouvre dans leur navigateur.',
      },
      {
        question: 'Les clients doivent-ils installer une application ?',
        answer:
          'Non. Vos clients ouvrent le menu dans le navigateur de leur téléphone, sans application à télécharger ni compte à créer.',
      },
      {
        question: 'Puis-je modifier les plats et les prix après publication ?',
        answer:
          'Oui. Modifiez votre menu depuis votre téléphone ou votre ordinateur, puis publiez la mise à jour. Le même QR code imprimé affiche alors la dernière version.',
      },
      {
        question: 'Puis-je indiquer les allergènes et proposer le menu en plusieurs langues ?',
        answer:
          'Oui. Vous pouvez indiquer les 14 allergènes de l’UE, permettre aux clients de filtrer les plats et proposer votre menu en anglais, allemand, espagnol, français, italien et roumain.',
      },
      {
        question: 'Puis-je créer gratuitement un menu QR code ?',
        answer:
          'Oui. Le forfait gratuit comprend un menu de cinq plats maximum, ne demande aucune carte bancaire et n’a pas de limite de temps.',
      },
    ],
  },
  finalCta: {
    heading: {
      line1: 'Essayez-le tout de suite -',
      highlight: 'scannez ce code.',
    },
    paragraph:
      'C’est exactement ce que feront vos clients à table. Si vous aimez ce que vous voyez, votre propre menu est à environ quinze minutes.',
    cta: 'Créez votre menu dès aujourd’hui',
  },
  jsonLd: {
    appDescription:
      'Menus QR code pour restaurants, cafés et bars. Créez un menu numérique en quelques minutes ou importez votre menu existant depuis un PDF ou des photos, mettez à jour les prix instantanément, programmez le changement de menu selon l’horaire, marquez des plats indisponibles, avec des étiquettes d’allergènes, un filtre d’allergènes pour les clients et la traduction du menu en six langues.',
  },
};
